import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User, Provider } from '@supabase/supabase-js';
import { AuthContextType } from '@/types/auth';
import {
  supabase,
  signInWithPassword,
  signInWithOAuth,
  signUp as supabaseSignUp,
  signOut as supabaseSignOut,
  resetPassword as supabaseResetPassword,
  updatePassword as supabaseUpdatePassword,
  isEmailVerificationRequired,
} from '@/lib/supabase';
import { toast } from 'sonner';
import { identifyUser, resetMixpanel } from '@/lib/mixpanel';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  useEffect(() => {
    // Initial session fetch
    const fetchSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user || null);
        setIsAuthenticated(!!currentSession);
        
        // Check if email is verified
        // Google OAuth users are automatically verified
        // Email users have an email_confirmed_at field when verified
        if (currentSession?.user) {
          const isVerified = 
            currentSession.user.app_metadata.provider === 'google' || 
            !!currentSession.user.email_confirmed_at;
          setIsEmailVerified(isVerified);
          
          // Identify user in Mixpanel if authenticated
          identifyUser(
            currentSession.user.id,
            currentSession.user.email ?? undefined,
            currentSession.user.user_metadata?.full_name ?? undefined
          );
        } else {
          setIsEmailVerified(false);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    // Set up listener for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user || null);
        setIsAuthenticated(!!currentSession);
        
        // Check if email is verified
        if (currentSession?.user) {
          const isVerified = 
            currentSession.user.app_metadata.provider === 'google' || 
            !!currentSession.user.email_confirmed_at;
          setIsEmailVerified(isVerified);
          
          // Identify user in Mixpanel if authenticated
          identifyUser(
            currentSession.user.id,
            currentSession.user.email ?? undefined,
            currentSession.user.user_metadata?.full_name ?? undefined
          );
        } else {
          setIsEmailVerified(false);
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await signInWithPassword(email, password);
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast.error(error.message || 'Error signing in');
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const { error } = await signInWithOAuth('google' as Provider);
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      toast.error(error.message || 'Error signing in with Google');
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseSignUp(email, password, metadata);
      if (error) throw error;
      return { error: null, data, emailVerificationRequired: isEmailVerificationRequired() };
    } catch (error: any) {
      console.error('Error signing up:', error);
      toast.error(error.message || 'Error signing up');
      return { error, data: null, emailVerificationRequired: isEmailVerificationRequired() };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabaseSignOut();
      resetMixpanel();
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error(error.message || 'Error signing out');
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const { error } = await supabaseResetPassword(email);
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast.error(error.message || 'Error resetting password');
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setLoading(true);
      const { error } = await supabaseUpdatePassword(password);
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error(error.message || 'Error updating password');
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async (deleteOrganization = false, confirmOrganizationName = '') => {
    try {
      setLoading(true);
      
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session?.access_token) {
        throw new Error('No valid session');
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({
          deleteOrganization,
          confirmOrganizationName
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete account');
      }
      
      // Sign out after successful deletion
      await supabaseSignOut();
      resetMixpanel();
      
      return { error: null, result };
    } catch (error: any) {
      console.error('Error deleting account:', error);
      return { error, result: null };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    user,
    loading,
    isAuthenticated,
    isEmailVerified,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};